import { useNavigate } from "react-router";
import { useAuth } from "@repo/auth";
import { useTranslation } from "@repo/i18n";
import { EmptyState } from "@repo/blocks";
import { PageScaffold } from "../shell/PageScaffold";
import { useVenue } from "../shell/VenueContext";
import { useEntitlementPlan } from "../shell/featureMeta";
import { canAccessVenue } from "../shell/roles";

/**
 * Shown in place of a whole-page feature the venue's plan does not include ([RequireEntitlement]). A
 * meaningful "not in your plan → upgrade" message rather than a raw error, with a path to Billing for those
 * who can actually change the plan (MANAGER+); everyone else sees why the feature is unavailable. The server
 * stays authoritative — this is presentation only.
 */
export function Upgrade({ feature }: { feature: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { venueId } = useVenue();
  const { claims } = useAuth();
  const { planName } = useEntitlementPlan(feature);
  const featureLabel = t(`entitlement.feature.${feature}`);
  const featureDesc = t(`entitlement.featureDesc.${feature}`);
  const canManage = canAccessVenue(claims, venueId, "MANAGER");
  // Name the plan that unlocks it when the catalog resolved it, else the generic upgrade line.
  const reason = planName
    ? t("entitlement.upgrade.needsPlan", { plan: planName })
    : t("entitlement.upgrade.description");
  return (
    <PageScaffold title={featureLabel}>
      <EmptyState
        title={t("entitlement.upgrade.title", { feature: featureLabel })}
        description={`${featureDesc} ${reason}`}
        action={
          canManage
            ? {
                label: t("entitlement.upgrade.cta"),
                onClick: () => navigate(`/v/${venueId}/billing`),
              }
            : undefined
        }
      />
    </PageScaffold>
  );
}
